import { useMemo } from 'react';

interface CardViewEventResult {
  name: string;
  attributes?: Record<string, string>;
}

interface ButtonClickEventResult {
  name: string;
  attributes: Record<string, string>;
}

export const useRefundCardEvents = (refundItem?: any, cardType: string = '', businessType: string = '') => {
  const typeAttribute = useMemo((): Record<string, string> => {
    if (cardType === 'indis-tritx') {
      return { type: 'capital-gain' };
    }
    if (['indis', 'indis-income'].includes(cardType)) {
      return { type: 'global-income' };
    }
    return {};
  }, [cardType]);

  const cardViewEvent = useMemo((): CardViewEventResult | undefined => {
    let name = 'home_body_status-apply-complete';
    let attributes: Record<string, string> | undefined;

    switch (refundItem?.refundStatus) {
      case 'ApplyPossible':
        name = 'home_body_status-apply-possible';
        attributes = { status: 'apply-possible', business: businessType };
        break;
      case 'ApplyComplete':
        if (cardType === 'indis-tritx') {
          name = 'home_body_status-applied';
          attributes = {
            business: businessType,
            ...typeAttribute
          };
          break;
        }
        if (refundItem?.cancelStage === 'Canceling') {
          name = 'home_body_status-cancel';
          attributes = { status: 'cancel', business: businessType, ...typeAttribute };
          break;
        }
        switch (refundItem?.refundStage) {
          case 'Review':
            name = 'home_body_status-review';
            switch (refundItem?.refundSubStage) {
              default:
              case 'Default':
                name = '';
                break;
              case 'ReviewCs':
                attributes = { status: 'hold', business: businessType };
                break;
              case 'ReviewWritingComplete':
                attributes = { status: 'success', business: businessType };
                break;
            }
            break;
          case 'Declare':
            name = 'home_body_status-declare';
            attributes = {
              status:
                refundItem?.refundSubStage === 'DeclareSpecialFeeA' || refundItem?.refundSubStage === 'DeclareSpecialFeeB' ? 'rural-tax' : 'declare-complete',
              business: businessType
            };
            break;
          case 'RefundPreDetermine':
            name = 'home_body_status-predetermine';
            break;
          case 'RefundDetermine':
            name = 'home_body_status-determine';
            break;
          case 'RefundCompleteA':
          case 'RefundCompleteB':
            name = 'home_body_status-refunded';
            attributes = { status: refundItem?.refundStage === 'RefundCompleteA' ? 'pay-available' : 'pay-unavailable', business: businessType };
            break;
          default:
            name = 'home_body_status-applied';
            attributes = {
              business: businessType,
              ...typeAttribute
            };
        }
        break;
      case 'RefundImpossible':
        name = 'home_body_status-declare';
        attributes = { status: 'impossible', business: businessType };
        break;
      case 'RefundFinish':
        name = 'home_body_status-paid';
        attributes = { status: 'pay-complete', business: businessType };
        break;
      default:
        return undefined;
    }

    return {
      name,
      attributes
    };
  }, [refundItem, cardType, businessType, typeAttribute]);

  const buttonClickEvent = useMemo((): ButtonClickEventResult => {
    let name =
      refundItem?.refundStatus === 'ApplyImpossible' || refundItem?.refundStatus === 'ApplyPossible' ? 'home_body_before-apply' : 'home_body_result-detail';
    let attributes: Record<string, string> = {};

    switch (refundItem?.refundStatus) {
      case 'ApplyImpossible':
        attributes = {
          'item-no': refundItem?.refundAmount === '0' ? 'none' : 'impossible',
          business: businessType,
          ...typeAttribute
        };
        break;
      case 'ApplyPossible':
        attributes = {
          'item-no': 'possible',
          business: businessType,
          ...typeAttribute
        };
        break;
      case 'Retry':
        name = 'home_body_lookup-restart';
        attributes = {
          business: businessType
        };
        break;
      default:
        attributes = {
          'item-no': cardType !== 'indis-tritx' ? refundItem?.refundId || '' : '',
          business: businessType
        };
        break;
    }

    return {
      name,
      attributes
    };
  }, [refundItem, cardType, businessType, typeAttribute]);

  return {
    cardViewEvent,
    buttonClickEvent
  };
};
